import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../components/PageHeader/PageHeader';
import SitesOrderService, { SITES_ORDER_DISPLAY_OFFSET } from '../../../services/SitesOrderService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import OrderDetailModal from './OrderDetailModal';
import { useWebSocket } from '../../../context/WebSocketContext';
import { fetchInvoiceTickets } from '../../../services/SalesService';
import { base64ToBlob } from '../../../utils/ImageUtils';
import { getSitesOrdersConfig } from '../../../services/SettingsService';
import { getLocations } from '../../../services/LocationService';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';
import { permissionOptions } from '../../../utils/Enums';
import { formatMoney } from '../../../utils/currency';

dayjs.extend(relativeTime);
dayjs.locale('es');

const Orders = () => {
  const { t } = useTranslation();
  const storedPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const manageSitesPermissionId = permissionOptions.find((permission) => permission.label === 'MANAGE_SITES')?.id;
  const viewSitesOrdersPermissionId = permissionOptions.find((permission) => permission.label === 'VIEW_SITES_ORDERS')?.id;
  const canManageSites = manageSitesPermissionId != null && storedPermissions.includes(manageSitesPermissionId);
  const canViewSitesOrders = canManageSites || (viewSitesOrdersPermissionId != null && storedPermissions.includes(viewSitesOrdersPermissionId));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [shippingStatusFilter, setShippingStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrderForMenu, setSelectedOrderForMenu] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [orderToCompletePayment, setOrderToCompletePayment] = useState(null);
  const [completingPayment, setCompletingPayment] = useState(false);
  const [paymentCompletionMethod, setPaymentCompletionMethod] = useState('TRANSFER');
  const [updatingShippingOrderId, setUpdatingShippingOrderId] = useState(null);
  const [deliveryIntegrationEnabled, setDeliveryIntegrationEnabled] = useState(true);
  const [locations, setLocations] = useState([]);
  const { subscribe, healthy: wsHealthy } = useWebSocket();
  const lastResultSignatureRef = useRef('');
  const fetchOrdersRef = useRef(null);
  const wsFollowupTimeoutRef = useRef(null);

  const shippingStatuses = [
    { value: 'PENDING', label: t('pending') || 'Pendiente' },
    { value: 'DELIVERED', label: t('delivered') || 'Entregado' },
    { value: 'CANCELLED', label: t('canceled') || 'Anulada' }
  ];

  const paymentStatuses = [
    { value: 'PENDING', label: t('pending') || 'Pendiente' },
    { value: 'COMPLETED', label: t('completed') || 'Finalizado' },
    { value: 'CANCELED', label: t('canceled') || 'Anulada' }
  ];

  const buildResultSignature = (rows, total) =>
    JSON.stringify({
      total: Number(total || 0),
      rows: (rows || []).map((item) => ({
        id: item?.id,
        updatedAt: item?.updatedAt || item?.updated_at || null,
        paymentStatus: item?.paymentStatus || null,
        shippingStatus: item?.shippingStatus || null,
        invoiceId: item?.invoiceId || null,
      })),
    });

  const fetchOrders = useCallback(async ({ silent = false, force = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const params = {
        page: paginationModel.page + 1, // Backend usa página base 1
        pageSize: paginationModel.pageSize,
        search: searchTerm || undefined,
        shippingStatus: shippingStatusFilter || undefined,
        paymentStatus: paymentStatusFilter || undefined
      };
      const response = await SitesOrderService.getOrders(params);
      const nextOrders = response.orders || [];
      const nextTotal = response.total || 0;
      const nextSignature = buildResultSignature(nextOrders, nextTotal);

      if (!force && silent && lastResultSignatureRef.current === nextSignature) {
        return;
      }

      lastResultSignatureRef.current = nextSignature;
      setOrders(nextOrders);
      setTotalRows(nextTotal);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (!silent) {
        SnackbarUtils.error(t('error_loading_orders') || 'Error al cargar las órdenes');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [paginationModel, searchTerm, shippingStatusFilter, paymentStatusFilter, t]);

  useEffect(() => {
    fetchOrdersRef.current = fetchOrders;
  }, [fetchOrders]);

  const triggerWsRefresh = useCallback(() => {
    fetchOrdersRef.current?.({ silent: true, force: true });
    if (wsFollowupTimeoutRef.current) {
      clearTimeout(wsFollowupTimeoutRef.current);
    }
    wsFollowupTimeoutRef.current = setTimeout(() => {
      fetchOrdersRef.current?.({ silent: true, force: true });
    }, 1200);
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!canViewSitesOrders) {
      setDeliveryIntegrationEnabled(true);
      return () => {};
    }

    let cancelled = false;
    getSitesOrdersConfig()
      .then((config) => {
        if (cancelled) return;
        setDeliveryIntegrationEnabled(config.deliveryIntegrationEnabled !== false);
      })
      .catch(() => {
        if (!cancelled) setDeliveryIntegrationEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canViewSitesOrders]);

  useEffect(() => {
    if (!canManageSites) {
      setLocations([]);
      return () => {};
    }

    let mounted = true;
    getLocations()
      .then((rows) => {
        if (!mounted) return;
        setLocations(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!mounted) return;
        setLocations([]);
      });
    return () => {
      mounted = false;
    };
  }, [canManageSites]);

  // Refrescar pedidos cuando lleguen notificaciones nuevas (estando en esta pantalla)
  useEffect(() => {
    const handleNewOrdersNotification = () => {
      triggerWsRefresh();
    };
    window.addEventListener('notifications-new-orders', handleNewOrdersNotification);
    return () => window.removeEventListener('notifications-new-orders', handleNewOrdersNotification);
  }, [triggerWsRefresh]);

  // Refrescar automáticamente cuando el backend confirma cambios de estado en una orden (ej. complete-after-payment).
  useEffect(() => {
    const unsubscribe = subscribe('sites.order.updated', () => {
      triggerWsRefresh();
    });
    return unsubscribe;
  }, [subscribe, triggerWsRefresh]);

  // Refresco directo por WS al entrar una notificación de orden.
  // Evita depender del puente "notifications-new-orders" del dropdown.
  useEffect(() => {
    const unsubscribe = subscribe('sites.notification.created', (data) => {
      if ((data?.type || '').toLowerCase() === 'order') {
        const rawLocationId = data.locationId ?? data.location_id ?? data.branchId;
        const locationId = Number(rawLocationId);
        if (Number.isFinite(locationId) && locations.length > 0) {
          const canSeeOrderLocation = locations.some((location) => Number(location.id) === locationId);
          if (!canSeeOrderLocation) return;
        }
        triggerWsRefresh();
      }
    });
    return unsubscribe;
  }, [subscribe, triggerWsRefresh, locations]);

  // Snapshot inmediato al recuperar WS.
  useEffect(() => {
    if (wsHealthy) {
      triggerWsRefresh();
    }
  }, [wsHealthy, triggerWsRefresh]);

  // Polling dinámico: 60s con WS saludable, 10s cuando WS está degradado/caído.
  useEffect(() => {
    const intervalMs = wsHealthy ? 60_000 : 10_000;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchOrders({ silent: true });
      }
    }, intervalMs);
    return () => clearInterval(interval);
  }, [fetchOrders, wsHealthy]);

  useEffect(() => {
    return () => {
      if (wsFollowupTimeoutRef.current) {
        clearTimeout(wsFollowupTimeoutRef.current);
      }
    };
  }, []);

  const getShippingStatusColor = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'SCHEDULED_HIDDEN': return 'warning';
      case 'PENDING': return 'warning';
      case 'DELIVERED': return 'success';
      case 'CANCELLED': return 'error';
      case 'CANCELED': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'COMPLETED': return 'success';
      case 'CANCELED': return 'error';
      default: return 'default';
    }
  };

  const getShippingStatusLabel = (status) => {
    const statusMap = {
      'SCHEDULED_HIDDEN': t('pending') || 'Pendiente',
      'PENDING': t('pending') || 'Pendiente',
      'DELIVERED': t('delivered') || 'Entregado',
      'CANCELLED': t('canceled') || 'Anulada',
      'CANCELED': t('canceled') || 'Anulada',
    };
    const normalized = (status || '').toUpperCase();
    return statusMap[normalized] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const statusMap = {
      'PENDING': t('pending') || 'Pendiente',
      'COMPLETED': t('completed') || 'Finalizado',
      'CANCELED': t('canceled') || 'Anulada',
      'UNPAID': t('pending') || 'Pendiente',
      'PARTIAL': t('pending') || 'Pendiente',
      'PAID': t('completed') || 'Finalizado',
      'REFUNDED': t('pending') || 'Pendiente'
    };
    return statusMap[status] || status;
  };

  const handleViewDetails = async (order) => {
    try {
      setLoadingDetail(true);
      const detail = await SitesOrderService.getOrderById(order.id);
      setOrderDetail(detail);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Error loading order details:', error);
      SnackbarUtils.error(t('error_loading_order_details') || 'Error al cargar los detalles del pedido');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleViewTicket = async (order) => {
    if (!order.invoiceId) {
      SnackbarUtils.warning(t('no_invoice_available') || 'No hay factura disponible para esta orden');
      return;
    }

    try {
      const response = await fetchInvoiceTickets(order.invoiceId);
      const ticketsData = response?.data?.data || response?.data || response || {};
      const facturaBase64 = ticketsData?.factura || null;
      const deliveryTicketBase64 = ticketsData?.deliveryTicket || null;

      const openPdfBase64 = (base64) => {
        if (!base64) return false;
        const blob = base64ToBlob(base64);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => {
          try {
            window.URL.revokeObjectURL(url);
          } catch (_) {
            // no-op
          }
        }, 60_000);
        return true;
      };

      const openedFactura = openPdfBase64(facturaBase64);
      const openedDelivery = openPdfBase64(deliveryTicketBase64);

      if (!openedFactura && !openedDelivery) {
        SnackbarUtils.warning(t('no_ticket_pdf_available') || 'No hay PDF disponible para este ticket');
      }
    } catch (error) {
      console.error('Error loading ticket:', error);
      SnackbarUtils.error(t('error_loading_ticket') || 'Error al cargar el ticket');
    }
  };

  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrderForMenu(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrderForMenu(null);
  };

  const handleCancelOrderClick = () => {
    if (selectedOrderForMenu) {
      setOrderToCancel(selectedOrderForMenu);
      setCancelDialogOpen(true);
      handleMenuClose();
    }
  };

  const canCompletePayment = (order) =>
    order?.paymentStatus === 'PENDING' &&
    order?.paymentStatus !== 'CANCELED';

  const handleOpenCompletePaymentDialog = (order) => {
    if (!order) return;
    const currentMethod = String(order.paymentMethod || '').toUpperCase();
    const nextMethod = ['CASH', 'TRANSFER', 'MERCADOPAGO', 'POS'].includes(currentMethod)
      ? currentMethod
      : 'TRANSFER';
    setOrderToCompletePayment(order);
    setPaymentCompletionMethod(nextMethod);
    setPaymentDialogOpen(true);
  };

  const handleCloseCompletePaymentDialog = () => {
    if (completingPayment) return;
    setPaymentDialogOpen(false);
    setOrderToCompletePayment(null);
    setPaymentCompletionMethod('TRANSFER');
  };

  const handleConfirmCompletePayment = async () => {
    if (!orderToCompletePayment) return;
    try {
      setCompletingPayment(true);
      const terminalCode = ['CASH', 'POS'].includes(paymentCompletionMethod)
        ? localStorage.getItem('terminalId')
        : null;
      await SitesOrderService.updatePaymentStatus(
        orderToCompletePayment.id,
        'COMPLETED',
        terminalCode,
        paymentCompletionMethod
      );
      SnackbarUtils.success(t('payment_status_updated') || 'Estado de pago actualizado');
      handleCloseCompletePaymentDialog();
      fetchOrders();
    } catch (error) {
      console.error('Error updating payment status:', error);
      SnackbarUtils.error(
        error?.response?.data?.message ||
        t('error_updating_payment_status') ||
        'Error al actualizar estado de pago'
      );
    } finally {
      setCompletingPayment(false);
    }
  };

  const handleCancelOrderConfirm = async () => {
    if (!orderToCancel) return;
    try {
      setCanceling(true);
      await SitesOrderService.cancelOrder(orderToCancel.id);
      SnackbarUtils.success(t('order_canceled_successfully') || 'Orden anulada correctamente');
      setCancelDialogOpen(false);
      setOrderToCancel(null);
      fetchOrders();
    } catch (error) {
      console.error('Error canceling order:', error);
      SnackbarUtils.error(error?.response?.data?.message || t('error_canceling_order') || 'Error al anular la orden');
    } finally {
      setCanceling(false);
    }
  };

  const canMarkPickupAsDelivered = (order) => (
    order?.deliveryType === 'PICKUP' &&
    !['DELIVERED', 'CANCELLED', 'CANCELED'].includes((order?.shippingStatus || '').toUpperCase())
  );

  const canMarkDeliveryManualStatus = (order, status) => {
    if (deliveryIntegrationEnabled) return false;
    if (order?.deliveryType !== 'DELIVERY') return false;
    const current = (order?.shippingStatus || '').toUpperCase();
    if (['CANCELLED', 'CANCELED'].includes(current)) return false;
    return current !== String(status || '').toUpperCase();
  };

  const handleUpdateShippingStatus = async (order, nextStatus) => {
    try {
      setUpdatingShippingOrderId(order.id);
      await SitesOrderService.updateShippingStatus(order.id, nextStatus);
      SnackbarUtils.success(t('shipping_status_updated') || 'Estado de envío actualizado');
      fetchOrders();
    } catch (error) {
      console.error('Error updating shipping status:', error);
      SnackbarUtils.error(
        error?.response?.data?.message ||
        t('error_updating_shipping_status') ||
        'Error al actualizar estado de envío'
      );
    } finally {
      setUpdatingShippingOrderId(null);
    }
  };

  const handleMarkPickupAsDelivered = async (order) => {
    if (!canMarkPickupAsDelivered(order)) return;
    await handleUpdateShippingStatus(order, 'DELIVERED');
  };

  return (
    <Box p={3}>
      <PageHeader
        title={t('orders') || 'Pedidos'}
        subtitle={
          canManageSites
            ? (t('orders_subtitle') || 'Gestiona los pedidos que tenés que resolver.')
            : 'Mirá los pedidos asignados a tus sucursales.'
        }
      />
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
            <TextField
              label={t('search') || 'Buscar'}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('payment_status') || 'Estado de Pago'}</InputLabel>
              <Select
                value={paymentStatusFilter}
                label={t('payment_status') || 'Estado de Pago'}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
              >
                <MenuItem value="">{t('all') || 'Todos'}</MenuItem>
                {paymentStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('shipping_status') || 'Estado de entrega'}</InputLabel>
              <Select
                value={shippingStatusFilter}
                label={t('shipping_status') || 'Estado de entrega'}
                onChange={(e) => setShippingStatusFilter(e.target.value)}
              >
                <MenuItem value="">{t('all') || 'Todos'}</MenuItem>
                {shippingStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <TableRefreshButton loading={loading} onClick={fetchOrders} />
          </Box>
        </Box>

        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ '& > *': { borderBottom: 1, borderColor: 'divider' } }}>
                  <TableCell align="center">{t('order_id') || 'Nº'}</TableCell>
                <TableCell>{t('customer') || 'Cliente'}</TableCell>
                  <TableCell align="right">{t('total') || 'Total'}</TableCell>
                <TableCell align="center">{t('payment_status') || 'Estado de Pago'}</TableCell>
                <TableCell align="center">{t('shipping_status') || 'Estado de entrega'}</TableCell>
                <TableCell align="center">{t('delivery_type') || 'Tipo'}</TableCell>
                <TableCell>{t('branch') || 'Sucursal'}</TableCell>
                <TableCell align="center">{t('order_date') || 'Fecha'}</TableCell>
                <TableCell align="center" sx={{ minWidth: 96, width: 96 }}>{t('actions') || 'Acciones'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((row) => {
                  const displayStatus = row.shippingStatus || 'PENDING';
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow sx={{ '& > *': { borderBottom: 1, borderColor: 'divider' } }}>
                        <TableCell align="center">{SITES_ORDER_DISPLAY_OFFSET + row.id}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="medium" noWrap sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.firstName} {row.lastName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatMoney(row.total)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Chip
                              label={getPaymentStatusLabel(row.paymentStatus)}
                              color={getPaymentStatusColor(row.paymentStatus)}
                              size="small"
                              sx={{
                                height: 22,
                                maxHeight: 22,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': { px: 1, py: 0.25 }
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Chip
                              label={getShippingStatusLabel(displayStatus)}
                              color={getShippingStatusColor(displayStatus)}
                              size="small"
                              sx={{
                                height: 22,
                                maxHeight: 22,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': { px: 1, py: 0.25 },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Chip
                              label={row.deliveryType === 'PICKUP' ? (t('pickup') || 'Retiro') : (t('delivery') || 'Envío')}
                              size="small"
                              color={row.deliveryType === 'PICKUP' ? 'info' : 'secondary'}
                              sx={{
                                height: 22,
                                maxHeight: 22,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': { px: 1, py: 0.25 }
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 140 }}>
                          <Typography variant="body2" noWrap sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.locationName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {dayjs(row.orderDate).format('DD/MM/YYYY HH:mm:ss')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 96, width: 96, whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)} title={t('more_options') || 'Más opciones'}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalRows}
          page={paginationModel.page}
          onPageChange={(_, newPage) => setPaginationModel((p) => ({ ...p, page: newPage }))}
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={(e) =>
            setPaginationModel((p) => ({ ...p, pageSize: parseInt(e.target.value, 10), page: 0 }))
          }
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage={t('rows_per_page') || 'Filas por página'}
        />
      </Paper>

      {/* Menu de acciones adicionales */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <>
            <MenuItem onClick={() => {
              if (selectedOrderForMenu) {
                handleViewDetails(selectedOrderForMenu);
                handleMenuClose();
              }
            }}>
              <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
              {t('view_details') || 'Ver detalles'}
            </MenuItem>
            {canCompletePayment(selectedOrderForMenu) && (
              <MenuItem
                onClick={() => {
                  handleOpenCompletePaymentDialog(selectedOrderForMenu);
                  handleMenuClose();
                }}
              >
                <DoneOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                {t('mark_payment_as_completed') || 'Marcar pago como finalizado'}
              </MenuItem>
            )}
            {selectedOrderForMenu?.invoiceId && (
              <MenuItem onClick={() => {
                handleViewTicket(selectedOrderForMenu);
                handleMenuClose();
              }}>
                <ReceiptIcon fontSize="small" sx={{ mr: 1 }} />
                {t('view_ticket') || 'Ver ticket'}
              </MenuItem>
            )}
            {canMarkPickupAsDelivered(selectedOrderForMenu) && (
              <MenuItem
                onClick={() => {
                  if (selectedOrderForMenu) {
                    handleMarkPickupAsDelivered(selectedOrderForMenu);
                    handleMenuClose();
                  }
                }}
                disabled={updatingShippingOrderId === selectedOrderForMenu?.id}
              >
                {updatingShippingOrderId === selectedOrderForMenu?.id ? (
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                ) : (
                  <DoneOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                )}
                {t('mark_as_delivered') || 'Marcar como entregado'}
              </MenuItem>
            )}
            {canMarkDeliveryManualStatus(selectedOrderForMenu, 'PENDING') && (
              <MenuItem
                onClick={() => {
                  if (selectedOrderForMenu) {
                    handleUpdateShippingStatus(selectedOrderForMenu, 'PENDING');
                    handleMenuClose();
                  }
                }}
                disabled={updatingShippingOrderId === selectedOrderForMenu?.id}
              >
                {updatingShippingOrderId === selectedOrderForMenu?.id ? (
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                ) : (
                  <DoneOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                )}
                Marcar envío como pendiente
              </MenuItem>
            )}
            {canMarkDeliveryManualStatus(selectedOrderForMenu, 'DELIVERED') && (
              <MenuItem
                onClick={() => {
                  if (selectedOrderForMenu) {
                    handleUpdateShippingStatus(selectedOrderForMenu, 'DELIVERED');
                    handleMenuClose();
                  }
                }}
                disabled={updatingShippingOrderId === selectedOrderForMenu?.id}
              >
                {updatingShippingOrderId === selectedOrderForMenu?.id ? (
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                ) : (
                  <DoneOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                )}
                Marcar envío como entregado
              </MenuItem>
            )}
            {selectedOrderForMenu?.paymentStatus !== 'CANCELED' && (
              <MenuItem
                onClick={handleCancelOrderClick}
                sx={{ color: 'error.main' }}
              >
                <CancelOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                {t('void_order') || 'Anular venta'}
              </MenuItem>
            )}
          </>
      </Menu>

      {/* Diálogo de confirmación para anular */}
      <Dialog open={cancelDialogOpen} onClose={() => !canceling && setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('void_order') || 'Anular venta'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('order') || 'Orden'}: #{orderToCancel?.id != null ? SITES_ORDER_DISPLAY_OFFSET + orderToCancel.id : '—'} - {orderToCancel?.firstName} {orderToCancel?.lastName}
          </Typography>
          <Typography variant="body2">
            {orderToCancel?.paymentStatus === 'PENDING'
              ? (t('void_order_confirm_pending') || 'Se devolverá el stock reservado y la orden quedará anulada.')
              : (t('void_order_confirm_paid') || 'Se devolverá el stock, se emitirá una Nota de Crédito y la orden quedará anulada.')
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('void_order_confirm_question') || '¿Continuar?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={canceling}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button
            onClick={handleCancelOrderConfirm}
            variant="contained"
            color="error"
            disabled={canceling}
            startIcon={canceling ? <CircularProgress size={16} /> : null}
          >
            {canceling ? (t('canceling') || 'Anulando...') : (t('void_order') || 'Anular venta')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentDialogOpen} onClose={handleCloseCompletePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('mark_payment_as_completed') || 'Marcar pago como finalizado'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('order') || 'Orden'}: #{orderToCompletePayment?.id != null ? SITES_ORDER_DISPLAY_OFFSET + orderToCompletePayment.id : '—'} - {orderToCompletePayment?.firstName} {orderToCompletePayment?.lastName}
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('payment_method') || 'Método de pago'}</InputLabel>
            <Select
              value={paymentCompletionMethod}
              label={t('payment_method') || 'Método de pago'}
              onChange={(e) => setPaymentCompletionMethod(e.target.value)}
            >
              <MenuItem value="CASH">{t('cash') || 'Efectivo'}</MenuItem>
              <MenuItem value="POS">POS</MenuItem>
              <MenuItem value="TRANSFER">{t('transfer') || 'Transferencia'}</MenuItem>
              <MenuItem value="MERCADOPAGO">MercadoPago</MenuItem>
            </Select>
          </FormControl>
          {['CASH', 'POS'].includes(paymentCompletionMethod) && !localStorage.getItem('terminalId') && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {t('terminal_not_configured') || 'Terminal no configurada'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompletePaymentDialog} disabled={completingPayment}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button
            onClick={handleConfirmCompletePayment}
            variant="contained"
            disabled={completingPayment || (['CASH', 'POS'].includes(paymentCompletionMethod) && !localStorage.getItem('terminalId'))}
          >
            {completingPayment ? (t('loading') || 'Procesando...') : (t('confirm') || 'Confirmar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de detalles */}
      <OrderDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setOrderDetail(null);
        }}
        order={orderDetail}
      />

    </Box>
  );
};

export default Orders;
